import './mailbox-profile.scss'

import { useMemo } from 'react'

import { Checkbox, Col, Row, Tooltip } from 'antd'
import classNames from 'classnames'
import { useFormik } from 'formik'

import { useCartMonitor } from 'app/providers'

import { Button } from 'common/components/Button/Button'
import { Input } from 'common/components/Input/Input'
import PhoneInput from 'common/components/PhoneInput/PhoneInput'
import { Select } from 'common/components/Select/Select'
import { Show } from 'common/components/Show/Show'
import { BUTTON_CONSTANTS } from 'common/constants/buttonConstants'
import { useFetchStatesQuery } from 'common/store/api/rootApi'

import { mailboxSchema } from 'features/SettingsOld/schemas/settingsMailboxSchema'

import { MailboxProfileText } from './profile.constants'
import type { MailboxProfileType } from './types'

const defaultValues: MailboxProfileType = {
  applicant_initials: '',
  applicant_home_address: '',
  applicant_home_city: '',
  applicant_home_state: null,
  applicant_home_zip: '',
  applicant_home_phone_number: '',
  business_name: '',
  business_address: '',
  business_city: '',
  business_state: null,
  business_zip: '',
  business_phone_number: '',
  business_type: '',
  is_forward_mailbox: false,
  forwarding_address: '',
  forwarding_city: '',
  forwarding_state: null,
  forwarding_zip: '',
  forwarding_phone_number: '',
}

type MailboxFormProps = {
  onSubmit: (values: MailboxProfileType, helpers: { resetForm: () => void }) => void
  containerClassName?: string
  data?: MailboxProfileType
  isLoading?: boolean
}

const MailboxForm = ({
  onSubmit,
  containerClassName,
  data,
  isLoading: isLoadingData = false,
}: MailboxFormProps) => {
  const { data: states, isLoading: isFetchingStates } = useFetchStatesQuery()
  const { isFetchingCart, isUpdatingCart } = useCartMonitor()

  const initialValues = useMemo(() => {
    if (data) return data
    return defaultValues
  }, [data])

  const isLoading = isFetchingStates || isLoadingData || isFetchingCart || isUpdatingCart

  const { values, errors, dirty, handleSubmit, handleChange, setFieldValue, resetForm } = useFormik(
    {
      initialValues,
      validationSchema: mailboxSchema,
      enableReinitialize: true,
      validateOnChange: true,
      onSubmit: (formValues) => onSubmit(formValues, { resetForm }),
    },
  )

  return (
    <section className={classNames('mailbox-container', containerClassName)}>
      <form onSubmit={handleSubmit}>
        <h2 className='section-margin'>{MailboxProfileText.APPLICANT}</h2>
        <Row gutter={[32, 16]}>
          <Col xs={24} md={12}>
            <Input
              isLoading={isLoading}
              maxLength={3}
              required
              label={MailboxProfileText.APPLICANT_INITIALS}
              name='applicant_initials'
              value={values.applicant_initials}
              onChange={handleChange}
              error={errors.applicant_initials}
            />
          </Col>
          <Col xs={24} md={12}>
            <Input
              isLoading={isLoading}
              required
              label={
                <Tooltip
                  title={`${MailboxProfileText.APPLICANT_HOME_ADDRESS} ${MailboxProfileText.NO_STREET_ADDRESS}`}>
                  <span>{MailboxProfileText.APPLICANT_HOME_ADDRESS}</span>
                </Tooltip>
              }
              name='applicant_home_address'
              value={values.applicant_home_address}
              onChange={handleChange}
              error={errors.applicant_home_address}
            />
          </Col>
          <Col xs={24} md={12}>
            <Input
              isLoading={isLoading}
              required
              label={MailboxProfileText.CITY}
              name='applicant_home_city'
              value={values.applicant_home_city}
              onChange={handleChange}
              error={errors.applicant_home_city}
            />
          </Col>
          <Col xs={24} md={12}>
            <Select
              isLoading={isLoading}
              required
              label={MailboxProfileText.STATE}
              listOptions={states}
              value={values.applicant_home_state}
              onChange={(value) => setFieldValue('applicant_home_state', value)}
              error={errors.applicant_home_state}
            />
          </Col>
          <Col xs={24} md={12}>
            <Input
              isLoading={isLoading}
              required
              label={MailboxProfileText.ZIP}
              name='applicant_home_zip'
              value={values.applicant_home_zip}
              onChange={handleChange}
              error={errors.applicant_home_zip}
            />
          </Col>
          <Col xs={24} md={12}>
            <PhoneInput
              isLoading={isLoading}
              required
              label={
                <Tooltip
                  title={`${MailboxProfileText.APPLICATION_PHONE} ${MailboxProfileText.INCLUDING_AREA_CODE}`}>
                  <span>{MailboxProfileText.APPLICATION_PHONE}</span>
                </Tooltip>
              }
              value={values.applicant_home_phone_number}
              onChange={(value) => setFieldValue('applicant_home_phone_number', value)}
              error={errors.applicant_home_phone_number}
            />
          </Col>
        </Row>

        <h2 className='section-margin'>Business</h2>
        <Row gutter={[32, 16]}>
          <Col xs={24} md={12}>
            <Input
              isLoading={isLoading}
              required
              label={MailboxProfileText.NAME_OF_CORPORATION}
              name='business_name'
              value={values.business_name}
              onChange={handleChange}
              error={errors.business_name}
            />
          </Col>
          <Col xs={24} md={12}>
            <Input
              isLoading={isLoading}
              required
              label={
                <Tooltip
                  title={`${MailboxProfileText.BUSINESS_ADDRESS} ${MailboxProfileText.NO_STREET_ADDRESS}`}>
                  <span>{MailboxProfileText.BUSINESS_ADDRESS}</span>
                </Tooltip>
              }
              name='business_address'
              value={values.business_address}
              onChange={handleChange}
              error={errors.business_address}
            />
          </Col>
          <Col xs={24} md={12}>
            <Input
              isLoading={isLoading}
              required
              label={MailboxProfileText.CITY}
              name='business_city'
              value={values.business_city}
              onChange={handleChange}
              error={errors.business_city}
            />
          </Col>
          <Col xs={24} md={12}>
            <Select
              isLoading={isLoading}
              required
              label={MailboxProfileText.STATE}
              listOptions={states}
              value={values.business_state}
              onChange={(value) => setFieldValue('business_state', value)}
              error={errors.business_state}
            />
          </Col>
          <Col xs={24} md={12}>
            <Input
              isLoading={isLoading}
              required
              label={MailboxProfileText.ZIP}
              name='business_zip'
              value={values.business_zip}
              onChange={handleChange}
              error={errors.business_zip}
            />
          </Col>
          <Col xs={24} md={12}>
            <PhoneInput
              isLoading={isLoading}
              required
              label={
                <Tooltip
                  title={`${MailboxProfileText.BUSINESS_PHONE} ${MailboxProfileText.INCLUDING_AREA_CODE}`}>
                  <span>{MailboxProfileText.BUSINESS_PHONE}</span>
                </Tooltip>
              }
              value={values.business_phone_number}
              onChange={(value) => setFieldValue('business_phone_number', value)}
              error={errors.business_phone_number}
            />
          </Col>
        </Row>

        <Row className='section-margin' gutter={[32, 16]}>
          <Col xs={24} md={12}>
            <Input
              isLoading={isLoading}
              required
              label={MailboxProfileText.TYPE_OF_BUSINESS}
              name='business_type'
              value={values.business_type}
              onChange={handleChange}
              error={errors.business_type}
            />
          </Col>
        </Row>

        <Row className='section-margin'>
          <Col>
            <Checkbox
              checked={values.is_forward_mailbox}
              onChange={(e) => setFieldValue('is_forward_mailbox', e.target.checked)}>
              {MailboxProfileText.FORWARDING_MAILBOX}
            </Checkbox>
          </Col>
        </Row>
        <Show when={values.is_forward_mailbox}>
          <h2 className='section-margin'>{MailboxProfileText.FORWARDING_MAILBOX}</h2>
          <Row gutter={[32, 16]}>
            <Col xs={24} md={12}>
              <Input
                isLoading={isLoading}
                required
                label={
                  <Tooltip
                    title={`${MailboxProfileText.FORWARDING_ADDRESS} ${MailboxProfileText.NO_STREET_ADDRESS}`}>
                    <span>{MailboxProfileText.FORWARDING_ADDRESS}</span>
                  </Tooltip>
                }
                name='forwarding_address'
                value={values.forwarding_address}
                onChange={handleChange}
                error={errors.forwarding_address}
              />
            </Col>
            <Col xs={24} md={12}>
              <Input
                isLoading={isLoading}
                required
                label={MailboxProfileText.CITY}
                name='forwarding_city'
                value={values.forwarding_city}
                onChange={handleChange}
                error={errors.forwarding_city}
              />
            </Col>
            <Col xs={24} md={12}>
              <Select
                isLoading={isLoading}
                required
                label={MailboxProfileText.STATE}
                listOptions={states}
                value={values.forwarding_state}
                onChange={(value) => setFieldValue('forwarding_state', value)}
                error={errors.forwarding_state}
              />
            </Col>
            <Col xs={24} md={12}>
              <Input
                isLoading={isLoading}
                required
                label={MailboxProfileText.ZIP}
                name='forwarding_zip'
                value={values.forwarding_zip}
                onChange={handleChange}
                error={errors.forwarding_zip}
              />
            </Col>
            <Col xs={24} md={12}>
              <PhoneInput
                isLoading={isLoading}
                required
                label={
                  <Tooltip
                    title={`${MailboxProfileText.FORWARDING_PHONE} ${MailboxProfileText.INCLUDING_AREA_CODE}`}>
                    <span>{MailboxProfileText.FORWARDING_PHONE}</span>
                  </Tooltip>
                }
                value={values.forwarding_phone_number}
                onChange={(value) => setFieldValue('forwarding_phone_number', value)}
                error={errors.forwarding_phone_number}
              />
            </Col>
          </Row>
        </Show>

        <Button
          className='section-margin'
          data-extra
          htmlType='submit'
          loading={isLoading}
          disabled={!dirty || isLoading}>
          {BUTTON_CONSTANTS.SAVE}
        </Button>
      </form>
    </section>
  )
}

export default MailboxForm
